import React, { useEffect, useMemo, useState } from "react";
import IsfSiteTemplateFields from "./IsfSiteTemplateFields";
import {
  applyStudySiteSetup,
  buildInitialPerSiteReferenceDates,
  buildStudySiteSetupPayload,
  defaultReferenceDateValue,
  getUniformTemplateNotice,
  resolvePerSiteTemplateSelection,
  validateStudySiteSetup,
} from "./isfSiteSetupService";
import utils from "../../../utils/utils";
import "./isfSiteSetupWizard.scss";

export default function IsfSiteSetupWizard({
  open,
  studyId,
  templateCategory,
  userData,
  studySites = [],
  templateOptions = [],
  onApplied,
  onClose,
}) {
  const [sameTemplateForAll, setSameTemplateForAll] = useState(true);
  const [sharedTemplate, setSharedTemplate] = useState(null);
  const [perSiteTemplates, setPerSiteTemplates] = useState({});
  const [sharedReferenceDate, setSharedReferenceDate] = useState(() =>
    defaultReferenceDateValue(),
  );
  const [perSiteReferenceDates, setPerSiteReferenceDates] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!open) return;
    setSameTemplateForAll(true);
    setSharedTemplate(null);
    setSharedReferenceDate(defaultReferenceDateValue());
    setError("");
    const initial = {};
    (studySites || []).forEach((site) => {
      initial[site.SiteId] = null;
    });
    setPerSiteTemplates(initial);
    setPerSiteReferenceDates(
      buildInitialPerSiteReferenceDates(studySites, defaultReferenceDateValue()),
    );
  }, [open, studySites]);

  const siteRows = useMemo(
    () => (studySites || []).filter((row) => row.SiteId),
    [studySites],
  );

  const templateSetupInfo = useMemo(
    () => getUniformTemplateNotice(siteRows, perSiteTemplates, sameTemplateForAll),
    [siteRows, perSiteTemplates, sameTemplateForAll],
  );

  const handlePerSiteTemplateChange = (siteId, opt) => {
    const result = resolvePerSiteTemplateSelection(
      siteRows,
      perSiteTemplates,
      siteId,
      opt,
    );
    setPerSiteTemplates(result.perSiteTemplates);
    setError("");
  };

  const handleApply = async () => {
    if (!studyId || !userData?.OrganizationId) return;
    setError("");

    const validation = validateStudySiteSetup({
      siteRows,
      sameTemplateForAllSites: sameTemplateForAll,
      sharedTemplateId: sharedTemplate?.value,
      perSiteTemplates,
      sharedReferenceDate,
      perSiteReferenceDates,
    });
    if (!validation.valid) {
      setError(validation.message);
      return;
    }

    const setupPayload = buildStudySiteSetupPayload({
      siteRows,
      sameTemplateForAllSites: sameTemplateForAll,
      sharedTemplateId: sharedTemplate?.value,
      perSiteTemplates,
      sharedReferenceDate,
      perSiteReferenceDates,
    });

    setSubmitting(true);
    try {
      const response = await applyStudySiteSetup({
        organizationId: userData.OrganizationId,
        studyId,
        templateCategory,
        createdBy: userData.StaffId,
        sameTemplateForAllSites: setupPayload.sameTemplateForAllSites,
        defaultTemplateId: setupPayload.defaultTemplateId,
        sites: setupPayload.sites,
      });
      if (response?.success !== 1) {
        throw new Error(
          utils.getApiResponseMessage(
            response,
            "Site setup failed. Please try again or contact your system administrator.",
          ),
        );
      }
      onApplied?.(setupPayload.sites);
    } catch (err) {
      setError(
        utils.sanitizeUserFacingMessage(
          err?.message,
          "Site setup failed. Please try again or contact your system administrator.",
        ),
      );
    } finally {
      setSubmitting(false);
    }
  };

  if (!open) return null;

  return (
    <div className="isf-site-setup-backdrop">
      <div className="isf-site-setup-modal">
        <div className="isf-site-setup-modal__head">
          <h5>eISF site setup</h5>
          <p>
            This study has multiple sites. Choose how templates are applied before
            opening the binder.
          </p>
        </div>

        <IsfSiteTemplateFields
          siteRows={siteRows}
          templateOptions={templateOptions}
          sameTemplateForAll={sameTemplateForAll}
          onSameTemplateForAllChange={setSameTemplateForAll}
          sharedTemplate={sharedTemplate}
          onSharedTemplateChange={setSharedTemplate}
          perSiteTemplates={perSiteTemplates}
          onPerSiteTemplateChange={handlePerSiteTemplateChange}
          sharedReferenceDate={sharedReferenceDate}
          onSharedReferenceDateChange={setSharedReferenceDate}
          perSiteReferenceDates={perSiteReferenceDates}
          onPerSiteReferenceDateChange={(siteId, value) =>
            setPerSiteReferenceDates((prev) => ({ ...prev, [siteId]: value }))
          }
          disabled={submitting}
          notice={templateSetupInfo}
        />

        {error ? <p className="text-danger small mb-0">{error}</p> : null}

        <div className="isf-site-setup-modal__actions">
          <button
            type="button"
            className="btn btn-outline-secondary btn-sm"
            onClick={onClose}
            disabled={submitting}
          >
            Cancel
          </button>
          <button
            type="button"
            className="btn btn-primary btn-sm"
            onClick={handleApply}
            disabled={submitting}
          >
            {submitting ? "Applying…" : "Apply setup"}
          </button>
        </div>
      </div>
    </div>
  );
}
